import { useEffect, useState } from 'react';
import type { POPreviewRow } from '@auto-oms/shared/types/batch';
import { api } from '../../lib/api';

const COLUMNS: { key: keyof POPreviewRow; label: string }[] = [
  { key: '품번코드',      label: '품번코드' },
  { key: '상품명',       label: '상품명' },
  { key: '수량',         label: '수량' },
  { key: '배송방식',     label: '배송방식' },
  { key: '받는분이름',   label: '받는분이름' },
  { key: '전화번호1',    label: '전화번호1' },
  { key: '주소1',        label: '주소1' },
  { key: '배송메세지',   label: '배송메세지' },
  { key: '사방넷주문번호', label: '사방넷 주문번호' },
  { key: '쇼핑몰주문번호', label: '쇼핑몰 주문번호' },
  { key: '관리메모',     label: '관리메모' },
];

interface Props {
  open: boolean;
  orderIds?: number[];
  onConfirm: (orderIds: number[]) => Promise<void>;
  onCancel: () => void;
}

export function POPreviewDialog({ open, orderIds, onConfirm, onCancel }: Props) {
  const [rows, setRows] = useState<POPreviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    if (rows.length === 0) return;
    setDownloading(true);
    try {
      const res = await fetch('/api/fulfillment/po/excel', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: rows.map((r) => r.orderId) }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `다운로드 실패 (status=${res.status})`);
      }
      const disposition = res.headers.get('Content-Disposition') ?? '';
      const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const asciiMatch = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = utf8Match?.[1]
        ? decodeURIComponent(utf8Match[1])
        : asciiMatch?.[1] ?? 'po.xlsx';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const qs = orderIds && orderIds.length > 0 ? `?orderIds=${orderIds.join(',')}` : '';
    api.get<{ rows: POPreviewRow[] }>(`/fulfillment/po/preview${qs}`)
      .then((res) => setRows(res.rows))
      .finally(() => setLoading(false));
  }, [open, orderIds]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="po-preview-title"
    >
      <div
        className="flex w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl bg-paper shadow-modal sm:rounded-2xl"
        style={{ maxHeight: '90vh' }}
      >
        <div className="border-b border-ink-200 px-5 pt-5 pb-4 sm:px-7 sm:pt-6 sm:pb-5">
          <p className="label-overline">Step 2 · 발주</p>
          <h2 id="po-preview-title" className="mt-2 text-[20px] font-medium tracking-[-0.01em] text-ink-950">
            발주 메일 미리보기
          </h2>
          <p className="mt-1 text-[13px] text-ink-500">
            실제 발송될 엑셀 내용입니다.
            {!loading && (
              <>
                {' '}
                <span className="font-semibold tabular text-ink-800">{rows.length}건</span>
              </>
            )}
          </p>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[13px] text-ink-400">
              불러오는 중…
            </div>
          ) : (
            <table className="w-full border-collapse text-[12px]">
              <thead className="sticky top-0 bg-ink-50 text-ink-700">
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className="whitespace-nowrap border-b border-ink-200 px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-500"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.orderId} className={i % 2 === 0 ? 'bg-paper' : 'bg-ink-50/50'}>
                    {COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className="max-w-[200px] truncate whitespace-nowrap border-b border-ink-100 px-2.5 py-1.5 text-ink-800"
                      >
                        {String(row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-[13px] text-ink-400">
                      수집완료 상태 주문이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-ink-200 bg-canvas px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <span className="text-[13px] text-ink-500">
            총{' '}
            <span className="tabular font-semibold text-ink-900">{rows.length}</span>건 발주
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy || downloading}>
              취소
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy || downloading || loading || rows.length === 0}
              onClick={handleDownload}
            >
              {downloading ? '다운로드 중…' : '엑셀 다운로드'}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || downloading || loading || rows.length === 0}
              onClick={async () => {
                setBusy(true);
                try {
                  await onConfirm(rows.map((r) => r.orderId));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? '발송 중…' : '메일 보내기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
